import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  // const [recentlyViewed, setRecentlyViewed] = useState([]);
  // const [priceAlerts, setPriceAlerts] = useState([]);
  // const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  // const moveToCart = (productId) => {
  //   const product = wishlist.find(item => item.id === productId);
  //   if (product) {
  //     addToCart(product);
  //     removeFromWishlist(productId);
  //   }
  // };

  // const addToRecentlyViewed = (product) => {
  //   setRecentlyViewed(prev => {
  //     const filtered = prev.filter(item => item.id !== product.id);
  //     return [product, ...filtered].slice(0, 10);
  //   });
  // };

  // // Price alerts
  // const setPriceAlert = (productId, targetPrice) => {
  //   setPriceAlerts(prev => [...prev, { productId, targetPrice, active: true }]);
  // };

  // // Loyalty points
  // const calculateLoyaltyPoints = (amount) => {
  //   const points = Math.floor(amount / 100); // 1 point per ₹100
  //   setLoyaltyPoints(prev => prev + points);
  //   return points;
  // };

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('mirae_cart');
    const savedOrders = localStorage.getItem('mirae_orders');
    const savedWishlist = localStorage.getItem('mirae_wishlist');

    if (savedCart) setCartItems(JSON.parse(savedCart));
    if (savedOrders) {
      const parsedOrders = JSON.parse(savedOrders);
      setOrders(parsedOrders.filter(order => order.status !== 'delivered'));
      setOrderHistory(parsedOrders.filter(order => order.status === 'delivered'));
    }
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('mirae_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const allOrders = [...orders, ...orderHistory];
    localStorage.setItem('mirae_orders', JSON.stringify(allOrders));
  }, [orders, orderHistory]);

  useEffect(() => {
    localStorage.setItem('mirae_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);

      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prevItems, {
        ...product,
        quantity,
        addedAt: new Date().toISOString()
      }];
    });

    setIsCartOpen(true);
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.finalPrice * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Order Management
  const createOrder = (orderDetails) => {
    const newOrder = {
      id: `ORD${Date.now()}`,
      ...orderDetails,
      items: cartItems,
      totalAmount: getCartTotal(),
      status: 'processing',
      paymentStatus: 'pending',
      orderDate: new Date().toISOString(),
      estimatedDelivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString()
    };

    setOrders(prevOrders => [...prevOrders, newOrder]);
    clearCart();
    return newOrder;
  };

  const updateOrderStatus = (orderId, status) => {
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? { ...order, status } : order
      )
    );

    // If order is delivered, move to history
    if (status === 'delivered') {
      const deliveredOrder = orders.find(order => order.id === orderId);
      if (deliveredOrder) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        setOrderHistory(prevHistory => [...prevHistory, { ...deliveredOrder, status: 'delivered' }]);
      }
    }
  };

  const cancelOrder = (orderId) => {
    setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
  };

  // Wishlist Management
  const addToWishlist = (product) => {
    setWishlist(prev => {
      if (!prev.find(item => item.id === product.id)) {
        return [...prev, product];
      }
      return prev;
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist(prev => prev.filter(item => item.id !== productId));
  };

  const isInWishlist = (productId) => {
    return wishlist.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      setIsCartOpen,
      orders,
      orderHistory,
      wishlist,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount,
      createOrder,
      updateOrderStatus,
      cancelOrder,
      addToWishlist,
      removeFromWishlist,
      isInWishlist
    }}>
      {children}
    </CartContext.Provider>
  );
};